import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);


const API_URL = '/api';

// Reusable Searchable Dropdown Component with Auto-Focus on Search
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};


// Status options
const STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
];

// Inventory flag options
const INVENTORY_FLAG_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const initialHeader = {
  issueId: '',
  issueDate: new Date().toISOString().split('T')[0],
  productionOrderId: '',
  productionOrderQty: '',
  inventoryFlag: 'yes',
  branchCode: '',
  issuedByUserId: '',
  issuedByUserName: '',
  department_id: "", // Add department_id field
};

const initialDetail = {
  productionOrderDetailId: '',
  materialType: '',
  productCode: '',
  productName: '',
  qty: '',
  calculatedQty: '',
  flushType: '',
  uom: '',
  warehouse: '',
  batchNumber: '',
  expiryDate: '',
  isCalculatedQtyModified: false,
  originalCalculatedQty: '',
  showBatchPopup: false,
  availableBatches: [],
  batchError: '',
  originalBatchNumber: '',
  originalExpiryDate: '',
  isBatchModified: false,
  allowBatchOnProduct: false,
  allowProductNegativeInventory: false,
  batchQuantity: 0,
  rate: '', // Add this
  value: '',  // Add this
  department_id: "", // Add department_id field
  trackingType: 'none', // Add tracking type: 'none', 'batch', or 'serial'
  batches: [], // Add for batch tracking
  serials: [], // Add for serial tracking
  showBatchSelection: false, // Add for batch selection popup
  showSerialSelection: false, // Add for serial selection popup
   availableSerials: [],
   serialSelectionAttempted: false, // Add this flag
};

// Batch Selection Popup Component (unchanged)
const BatchSelectionPopup = ({ 
  open, 
  onClose, 
  batchData, 
  onSelectBatch,
  productSettings,
  companySettings,
  requiredQuantity,
  selectedBatches = [],
  productCode,
  warehouseCode,
  warehouseFiltering
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedBatches, setTempSelectedBatches] = useState([]);
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize selected batches when popup opens
  // Initialize selected batches when popup opens
  useEffect(() => {
    if (open && !isInitialized) {
      console.log('Initializing BatchSelectionPopup with selectedBatches:', selectedBatches);
      console.log('Batch data:', batchData);
      
      // Ensure batchData is properly structured
      const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
      
      // Initialize with selected batches
      const initializedBatches = selectedBatches.map(selectedBatch => {
        // Find matching batch in available batches
        const batchInfo = batches.find(b => b.BatchNumber === selectedBatch.batchNumber);
        
        return {
          batchNumber: selectedBatch.batchNumber,
          quantity: selectedBatch.quantity || 0,
          expiryDate: selectedBatch.expiryDate || '',
          availableQuantity: batchInfo ? parseFloat(batchInfo.TotalQuantity) || 0 : 0
        };
      });
      
      console.log('Initialized batches:', initializedBatches);
      setTempSelectedBatches(initializedBatches);
      setErrors({});
      setIsInitialized(true);
    }
  }, [open, selectedBatches, batchData, isInitialized]);
  
  // Reset initialization state when popup closes
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);
  
  // Ensure batchData is properly structured before filtering
  const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
  
  // Add a safety check to ensure we only show batches for the current product
  const filteredBatches = batches.filter(batch => 
    batch.ItemCode === productCode || !batch.ItemCode // Include if ItemCode matches or is not present
  );
  
  
  // Filter batch data based on search term and quantity > 0
   const filteredBatchData = filteredBatches.filter(batch => {
    // Check if this batch is already selected
    const isSelected = tempSelectedBatches.some(selected => selected.batchNumber === batch.BatchNumber);
    
    // If batch is selected, include it regardless of quantity
    if (isSelected) {
      return true;
    }
    
    // For non-selected batches, filter out batches with zero or negative quantity
    const hasQuantity = parseFloat(batch.TotalQuantity) > 0;
    
    // Apply search filter if search term is provided
    const matchesSearch = searchTerm === '' || 
      (batch.BatchNumber && batch.BatchNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (batch.LocationName && batch.LocationName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (batch.WarehouseCode && batch.WarehouseCode.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return hasQuantity && matchesSearch;
  }).sort((a, b) => {
    // Sort so that selected batches appear first
    const aSelected = tempSelectedBatches.some(selected => selected.batchNumber === a.BatchNumber);
    const bSelected = tempSelectedBatches.some(selected => selected.batchNumber === b.BatchNumber);
    
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });

  const handleBatchSelect = (batch) => {
    const batchNumber = batch.BatchNumber;
    const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === batchNumber);
    
    if (existingIndex >= 0) {
      // Remove batch if already selected
      const newSelected = [...tempSelectedBatches];
      newSelected.splice(existingIndex, 1);
      setTempSelectedBatches(newSelected);
      
      // Clear any error for this batch
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[batchNumber];
        return newErrors;
      });
    } else {
      // Add new batch with quantity 0
      setTempSelectedBatches([
        ...tempSelectedBatches,
        {
          batchNumber: batch.BatchNumber,
          quantity: 0,
          expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : '',
          availableQuantity: parseFloat(batch.TotalQuantity) || 0
        }
      ]);
    }
  };

  const handleQuantityChange = (batchNumber, quantity) => {
    const newSelected = tempSelectedBatches.map(batch => {
      if (batch.batchNumber === batchNumber) {
        const qty = parseFloat(quantity) || 0;
        const availableQty = batch.availableQuantity;
        
        // Validate quantity doesn't exceed available
        if (qty > availableQty) {
          setErrors(prev => ({
            ...prev,
            [batchNumber]: `Quantity cannot exceed available quantity (${availableQty})`
          }));
          return batch;
        } else if (qty <= 0) {
          setErrors(prev => ({
            ...prev,
            [batchNumber]: "Quantity must be greater than zero"
          }));
          return batch;
        } else {
          // Clear error for this batch
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[batchNumber];
            return newErrors;
          });
          return { ...batch, quantity: qty };
        }
      }
      return batch;
    });
    
    setTempSelectedBatches(newSelected);
  };

  // Handle save with useCallback for stable reference
  const handleSave = useCallback(() => {
    // First, check for zero quantities and set errors for those
    const zeroQuantityErrors = {};
    let hasZeroQuantity = false;
    
    tempSelectedBatches.forEach(batch => {
      if (batch.quantity <= 0) {
        zeroQuantityErrors[batch.batchNumber] = "Quantity must be greater than zero";
        hasZeroQuantity = true;
      }
    });
    
    if (hasZeroQuantity) {
      setErrors(prev => ({ ...prev, ...zeroQuantityErrors }));
      return;
    }

    // Calculate total selected quantity
    const totalSelected = tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0);
    
    // Validate total matches required quantity
    if (Math.abs(totalSelected - requiredQuantity) > 0.001) {
      alert(`Total batch quantity (${totalSelected}) must match item quantity (${requiredQuantity})`);
      return;
    }
    
    // Check for any other errors
    const hasOtherErrors = Object.keys(errors).some(batchNumber => 
      tempSelectedBatches.some(batch => batch.batchNumber === batchNumber)
    );
    
    if (hasOtherErrors) {
      alert('Please fix the errors before saving');
      return;
    }
    
    onSelectBatch(tempSelectedBatches);
  }, [tempSelectedBatches, requiredQuantity, errors, onSelectBatch]);

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredBatchData.length > 0) {
        const firstBatch = filteredBatchData[0];
        const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === firstBatch.BatchNumber);
        
        if (existingIndex < 0) {
          // Add new batch with quantity 0
          setTempSelectedBatches(prev => [
            ...prev,
            {
              batchNumber: firstBatch.BatchNumber,
              quantity: 0,
              expiryDate: firstBatch.ExpiryDate ? new Date(firstBatch.ExpiryDate).toISOString().split('T')[0] : '',
              availableQuantity: parseFloat(firstBatch.TotalQuantity) || 0
            }
          ]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleSave]);

  // Early return after all hooks are defined
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Batches</h2>
            {/* Warehouse filtering info */}
            {warehouseFiltering && (
              <div className="mt-2 text-sm text-gray-600">
                {warehouseFiltering.enabled ? (
                  warehouseFiltering.applied ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing batches from: {warehouseFiltering.warehouseCode}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing batches from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required Quantity: {requiredQuantity}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by batch number..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {filteredBatchData.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity to Use</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBatchData.map((batch) => {
                    const selectedBatch = tempSelectedBatches.find(b => b.batchNumber === batch.BatchNumber);
                    const isSelected = !!selectedBatch;
                    
                    return (
                      <tr key={batch.BatchNumber} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleBatchSelect(batch)}
                            className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {productCode || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {batch.BatchNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="font-medium">{batch.LocationName || 'N/A'}</span>
                            {batch.WarehouseCode && (
                              <span className="ml-2 text-xs text-gray-400">({batch.WarehouseCode})</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.ExpiryDate ? new Date(batch.ExpiryDate).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.TotalQuantity}
                          {parseFloat(batch.TotalQuantity) <= 0 && (
                            <span className="ml-2 text-xs text-red-500 font-medium">
                              (Insufficient Stock)
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {isSelected ? (
                            <div className="flex items-center">
                              <input
                                type="number"
                                min="0"
                                max={batch.TotalQuantity}
                                value={selectedBatch.quantity}
                                onChange={(e) => handleQuantityChange(batch.BatchNumber, e.target.value)}
                                className={`w-20 border rounded px-2 py-1 ${
                                  errors[batch.BatchNumber] ? 'border-red-500' : 'border-gray-300'
                                }`}
                              />
                              {errors[batch.BatchNumber] && (
                                <span className="text-xs text-red-500 ml-2">
                                  {errors[batch.BatchNumber]}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">Select batch</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching batches found' : 'No batches found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? 'There are no batches matching your search criteria with available quantity.' 
                  : warehouseFiltering?.enabled && warehouseFiltering?.applied
                    ? `There are no batches available for this product in warehouse: ${warehouseFiltering.warehouseCode}`
                    : 'There are no batches available for this product.'}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedBatches.length} batches | 
              Total Quantity: {tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0)}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Selection Popup Component
const SerialSelectionPopup = ({ 
  serials = [], // Default to empty array
  onSelect, 
  onClose, 
  warehouseWise, 
  warehouseName,
  selectedSerials = [], // Default to empty array
  requiredQuantity,
  allowNegative,
  productAllowNegative,
  isEdit,
  productCode
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedSerials, setTempSelectedSerials] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Debug: Log the props
  console.log('SerialSelectionPopup props:', {
    serials,
    selectedSerials,
    productCode,
    warehouseName
  });
  
  // Initialize selected serials when popup opens
 // Initialize selected serials when popup opens
  useEffect(() => {
    if (!isInitialized) {
      console.log('Initializing SerialSelectionPopup with selectedSerials:', selectedSerials);
      console.log('Available serials:', serials);
      
      // Initialize with selected serials
      const initializedSerials = selectedSerials.map(serial => serial.SerialNumber);
      setTempSelectedSerials(initializedSerials);
      setIsInitialized(true);
    }
  }, [selectedSerials, serials, isInitialized]);
  
  // Reset initialization state when popup closes
  useEffect(() => {
    if (!serials || serials.length === 0) {
      setIsInitialized(false);
    }
  }, [serials]);
  
  // Filter serials based on search term
  const filteredSerials = useMemo(() => {
    if (!searchTerm) return serials;
    const term = searchTerm.toLowerCase();
    
    return serials.filter(serial => {
      const serialNumber = (serial.SerialNumber || '').toLowerCase();
      const locationName = (serial.LocationName || '').toLowerCase();
      
      return serialNumber.includes(term) || locationName.includes(term);
    });
  }, [serials, searchTerm]);
  
  // Handle serial selection
  const handleSerialSelect = (serialNumber) => {
    if (tempSelectedSerials.includes(serialNumber)) {
      setTempSelectedSerials(tempSelectedSerials.filter(s => s !== serialNumber));
    } else {
      setTempSelectedSerials([...tempSelectedSerials, serialNumber]);
    }
  };

  // Handle save
  const handleSave = () => {
    if (tempSelectedSerials.length !== requiredQuantity) {
      alert(`Please select exactly ${requiredQuantity} serials. Currently selected: ${tempSelectedSerials.length}`);
      return;
    }
    
    const serialObjects = tempSelectedSerials.map(serialNumber => ({
      SerialNumber: serialNumber
    }));
    
    onSelect(serialObjects);
  };

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredSerials.length > 0) {
        const firstSerial = filteredSerials[0];
        if (!tempSelectedSerials.includes(firstSerial.SerialNumber)) {
          setTempSelectedSerials(prev => [...prev, firstSerial.SerialNumber]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSave]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Serials</h2>
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Product:</span> 
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {productCode || 'N/A'}
              </span>
            </div>
            {warehouseWise && (
              <div className="mt-1 text-sm text-gray-600">
                {warehouseWise ? (
                  warehouseName ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing serials from: {warehouseName}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing serials from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required: {requiredQuantity} | Selected: {tempSelectedSerials.length}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by serial number, location, or warehouse..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Showing {filteredSerials.length} of {serials?.length || 0} serials
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {filteredSerials.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSerials.map((serial) => (
                    <tr key={`${serial.SerialNumber}-${Math.random()}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={tempSelectedSerials.includes(serial.SerialNumber)}
                          onChange={() => handleSerialSelect(serial.SerialNumber)}
                          className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {productCode || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {serial.SerialNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="font-medium">{serial.LocationName || 'N/A'}</span>
                          {serial.WarehouseCode && (
                            <span className="ml-2 text-xs text-gray-400">({serial.WarehouseCode})</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching serials found' : 'No serials found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `There are no serials matching "${searchTerm}".` 
                  : warehouseWise
                    ? `There are no serials available for product ${productCode} in warehouse: ${warehouseName}`
                    : `There are no serials available for product ${productCode}.`}
              </p>
            </div>
          )}
          
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedSerials.length} of {requiredQuantity} required
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IssueForProductionFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  const [details, setDetails] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    productionOrders: [],
    warehouses: [],
    branches: [],
    nextIssueNumber: '',
    nextDetailNumber: '',
    products: [],
  });
  const [selectedProductionOrder, setSelectedProductionOrder] = useState(null);
  const [productionOrderDetails, setProductionOrderDetails] = useState([]);
  const [loadingProductionOrderDetails, setLoadingProductionOrderDetails] = useState(false);
  const [dropdownError, setDropdownError] = useState(null);
  
  // New state for maximize/minimize functionality
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const modalRef = useRef(null);
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);


  const [loadingBatches, setLoadingBatches] = useState(false);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    AllowNegativeInventory: false,
    AllowWarehouseNegativeInventory: false
  });
  
  // State to track original production order quantity
  const [originalProductionOrderQty, setOriginalProductionOrderQty] = useState(0);
  
  // State for API error details
  const [apiError, setApiError] = useState(null);
  
  // New state for stock validation
  const [stockValidation, setStockValidation] = useState({});
  const [validatingStock, setValidatingStock] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // State for batch and serial selection popups
  const [showBatchSelection, setShowBatchSelection] = useState(false);
  const [showSerialSelection, setShowSerialSelection] = useState(false);
  const [selectedDetailIndex, setSelectedDetailIndex] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [serialData, setSerialData] = useState([]);
  
  const now = new Date().toISOString(); 
  
  // Function to toggle maximize state
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };
  
  // Function to toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };
  
  // Function to restore modal to normal size
  const restoreModal = () => {
    setIsMaximized(false);
    setIsMinimized(false);
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/company/settings`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await axios.get(`${API_URL}/departments`);
      const data = await response.data;
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`);
      const data = await response.data;
      console.log('Fetched screen configurations:', data);
      
      // Find the IssueForProduction screen configuration
      const issueForProductionScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'IssueForProduction'.toLowerCase()
      );
      
      console.log('IssueForProduction screen configuration:', issueForProductionScreen);
      if (issueForProductionScreen) {
        setScreenConfig(issueForProductionScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = issueForProductionScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = issueForProductionScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Function to fetch batches for a product
// Update the fetchBatchesForProduct function
const fetchBatchesForProduct = async (productCode, warehouseCode, idx) => {
  try {
    const token = localStorage.getItem('authToken');
    console.log(`Fetching batches for product: ${productCode}, warehouse: ${warehouseCode}`);
    
    const response = await axios.get(`${API_URL}/products/${productCode}/batches`, {
      params: { warehouseCode },
      headers: { 'Authorization': `Bearer ${token}` }
    });

    console.log("Batches API response:", response.data);
    
    // The response is an object with a batches property
    const batches = response.data && response.data.batches && Array.isArray(response.data.batches) 
      ? response.data.batches 
      : [];
    
    console.log('Processed batches:', batches);
    
    const newDetails = [...details];
    newDetails[idx].availableBatches = batches;
    setDetails(newDetails);
    
    return batches;
  } catch (error) {
    console.error('Error fetching batches:', error);
    const newDetails = [...details];
    newDetails[idx].availableBatches = []; // Set to empty array on error
    setDetails(newDetails);
    return [];
  }
};
  
  // Function to fetch serials for a product
const fetchSerialsForProduct = async (productCode, warehouseCode, idx) => {
  try {
    const token = localStorage.getItem('authToken');
    console.log(`Fetching serials for product: ${productCode}, warehouse: ${warehouseCode}`);
    
    const response = await axios.get(`${API_URL}/products/${productCode}/serials`, {
      params: { warehouseCode },
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    console.log('Fetched serials response:', response.data);
    
    // Handle different response formats
    let serials = [];
    if (Array.isArray(response.data)) {
      serials = response.data;
    } else if (response.data && response.data.serials && Array.isArray(response.data.serials)) {
      serials = response.data.serials;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      serials = response.data.data;
    }
    
    console.log('Processed serials:', serials);
    
    const newDetails = [...details];
    newDetails[idx].availableSerials = serials;
    setDetails(newDetails);
    
    return serials;
  } catch (error) {
    console.error('Error fetching serials:', error);
    const newDetails = [...details];
    newDetails[idx].availableSerials = []; // Set to empty array on error
    setDetails(newDetails);
    return [];
  }
};
  
  // Function to handle batch field click
const handleBatchFieldClick = async (idx) => {
  const detail = details[idx];
  
  // Check if product and warehouse are selected
  if (!detail.productCode) {
    const newDetails = [...details];
    newDetails[idx].batchError = 'Please select a product first';
    setDetails(newDetails);
    return;
  }
  
  if (!detail.warehouse) {
    const newDetails = [...details];
    newDetails[idx].batchError = 'Please select a warehouse first';
    setDetails(newDetails);
    return;
  }
  
  setSelectedDetailIndex(idx);
  setLoadingBatches(true);
  
  try {
    await fetchBatchesForProduct(detail.productCode, detail.warehouse, idx);
  } finally {
    setLoadingBatches(false);
    setShowBatchSelection(true);
  }
};
  
  // Function to handle serial field click
const handleSerialFieldClick = async (idx) => {
  const detail = details[idx];
  
  // Check if product and warehouse are selected
  if (!detail.productCode) {
    const newDetails = [...details];
    newDetails[idx].serialError = 'Please select a product first';
    setDetails(newDetails);
    return;
  }
  
  if (!detail.warehouse) {
    const newDetails = [...details];
    newDetails[idx].serialError = 'Please select a warehouse first';
    setDetails(newDetails);
    return;
  }
  
  setSelectedDetailIndex(idx);
  
  // Always fetch fresh serial data when opening the popup
  console.log('Fetching serials for popup...');
  await fetchSerialsForProduct(detail.productCode, detail.warehouse, idx);
  
  // Get the updated detail after fetching
  const updatedDetail = details[idx];
  console.log('Updated detail after fetch:', updatedDetail);
  
  // Set serial data for the popup
  setSerialData(updatedDetail.availableSerials || []);
  setShowSerialSelection(true);
};

const handleSerialPopupClose = () => {
  if (selectedDetailIndex !== null) {
    const newDetails = [...details];
    newDetails[selectedDetailIndex] = {
      ...newDetails[selectedDetailIndex],
      serialSelectionAttempted: true // Mark as attempted even if canceled
    };
    setDetails(newDetails);
  }
  setShowSerialSelection(false);
};
  
  // Function to handle batch selection
 const handleBatchSelect = (selectedBatches) => {
  if (selectedDetailIndex !== null) {
    const newDetails = [...details];
    const detail = newDetails[selectedDetailIndex];
    
    // Calculate total batch quantity
    const totalBatchQty = selectedBatches.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0);
    
    // Update detail with selected batches
    newDetails[selectedDetailIndex] = {
      ...detail,
      batches: selectedBatches, // Array of { batchNumber, quantity, expiryDate }
      calculatedQty: totalBatchQty.toString(),
      isCalculatedQtyModified: true
    };
    
    setDetails(newDetails);
    setShowBatchSelection(false);
  }
};
  
  // Function to handle serial selection
const handleSerialSelect = (selectedSerials) => {
  console.log('handleSerialSelect called with:', selectedSerials);
  
  if (selectedDetailIndex !== null) {
    const newDetails = [...details];
    const detail = newDetails[selectedDetailIndex];
    
    // Update detail with selected serials
    newDetails[selectedDetailIndex] = {
      ...detail,
      serials: selectedSerials,
      calculatedQty: selectedSerials.length.toString(),
      isCalculatedQtyModified: true,
      serialSelectionAttempted: true // Mark as attempted
    };
    
    console.log('Updated detail:', newDetails[selectedDetailIndex]);
    
    setDetails(newDetails);
    setShowSerialSelection(false);
  }
};
  
  // Function to close batch popup
  const closeBatchPopup = (idx) => {
    const newDetails = [...details];
    newDetails[idx].showBatchPopup = false;
    setDetails(newDetails);
  };
  
  // Function to validate batch quantity
  const validateBatchQuantity = (idx, detail) => {
    // If no batch is selected, no validation needed
    if (!detail.batchNumber) return { valid: true, message: '', availableQuantity: 0 };
    
    // Skip validation if both company and product allow negative inventory
    if (companySettings.AllowNegativeInventory && detail.allowProductNegativeInventory) {
      return { 
        valid: true, 
        message: 'Negative inventory allowed. No validation required.', 
        availableQuantity: 0 
      };
    }
    
    // Find the selected batch
    const selectedBatch = detail.availableBatches.find(
      batch => batch.BatchNumber === detail.batchNumber && 
              (detail.expiryDate ? 
                (batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] === detail.expiryDate : false) 
                : !batch.ExpiryDate)
    );
    
    if (!selectedBatch) return { valid: true, message: '', availableQuantity: 0 };
    
    const availableQuantity = selectedBatch.TotalQuantity || 0;
    const originalQuantity = parseFloat(detail.originalCalculatedQty) || 0;
    const newQuantity = parseFloat(detail.calculatedQty) || 0;
    const qtyChange = newQuantity - originalQuantity;
    
    // If quantity is decreased or unchanged, no need to validate
    if (qtyChange <= 0) {
      return { 
        valid: true, 
        message: `Quantity change is valid. Available: ${availableQuantity}`, 
        availableQuantity 
      };
    }
    
    // Only validate if quantity is increased
    if (qtyChange > availableQuantity) {
      return { 
        valid: false, 
        message: `Insufficient stock for batch ${detail.batchNumber}. Available: ${availableQuantity}, Requested increase: ${qtyChange}`, 
        availableQuantity 
      };
    }
    
    return { 
      valid: true, 
      message: `Available: ${availableQuantity}`, 
      availableQuantity 
    };
  };
  
  // Function to validate stock for a specific item using the stock/check API
 const validateItemStock = async (itemCode, location, quantity, idx, isQuantityModified, originalQuantity, allowProductNegativeInventory) => {
  if (!itemCode || !location) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: true, message: '' }
    }));
    return;
  }
  
  // Skip validation if both company and product allow negative inventory
  if (companySettings.AllowNegativeInventory && allowProductNegativeInventory) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { 
        valid: true, 
        message: 'Negative inventory allowed. No validation required.' 
      }
    }));
    return;
  }
  
  // If we're in edit mode and the quantity hasn't been modified, skip validation
  if (isEdit && !isQuantityModified) {
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: true, message: '' }
    }));
    return;
  }
  
  setValidatingStock(true);
  try {
    const token = localStorage.getItem('authToken');
    
    // Calculate the change in quantity
    const originalQty = parseFloat(originalQuantity) || 0;
    const newQty = parseFloat(quantity) || 0;
    const qtyChange = newQty - originalQty;
    
    console.log('Validating stock:', { 
      itemCode, 
      location, 
      qtyChange,
      originalQty,
      newQty,
      isEdit,
      isQuantityModified,
      allowProductNegativeInventory
    });
    
    // If quantity is decreased or unchanged, no need to validate
    if (qtyChange <= 0) {
      setStockValidation(prev => ({
        ...prev,
        [idx]: { valid: true, message: '' }
      }));
      return;
    }
    
    // Only validate if quantity is increased
    const response = await axios.get(`${API_URL}/stock/check`, {
      params: {
        itemCode,
        locationCode: location,
        asOfDate: new Date().toISOString(),
        quantity: qtyChange // Only validate the increase
      },
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const { 
      currentStock, 
      allowed, 
      message,
      companySettings: responseCompanySettings,
      productSettings: responseProductSettings
    } = response.data;
    
    console.log('Stock validation response:', response.data);
    
    setStockValidation(prev => ({
      ...prev,
      [idx]: { 
        valid: allowed, 
        message: message, 
        currentStock,
        companySettings: responseCompanySettings,
        productSettings: responseProductSettings
      }
    }));
  } catch (error) {
    console.error('Error validating stock:', error);
    
    let errorMessage = 'Error checking stock availability';
    
    if (error.response) {
      console.error('Error response:', error.response.data);
      
      // Extract detailed error information
      if (error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        if (error.response.data.error) {
          errorMessage += `: ${error.response.data.error}`;
        }
        if (error.response.data.stack) {
          console.error('Error stack:', error.response.data.stack);
        }
      }
    } else if (error.request) {
      errorMessage = 'No response from server';
    } else {
      errorMessage = error.message;
    }
    
    console.log('Setting error message:', errorMessage);
    
    setStockValidation(prev => ({
      ...prev,
      [idx]: { valid: false, message: errorMessage }
    }));
  } finally {
    setValidatingStock(false);
  }
};
  
  // Calculate summary values
  const summary = useMemo(() => {
    let totalQty = 0;
    let totalCalculatedQty = 0;
    
    details.forEach(detail => {
      const qty = parseFloat(detail.qty) || 0;
      const calculatedQty = parseFloat(detail.calculatedQty) || 0;
      totalQty += qty;
      totalCalculatedQty += calculatedQty;
    });
    
    return {
      totalQty: totalQty.toString(),
      totalCalculatedQty: totalCalculatedQty.toString(),
      productionOrderQty: header.productionOrderQty || '0'
    };
  }, [details, header.productionOrderQty]);
  
  // Check if form is valid
 const isFormValid = useMemo(() => {
  if (details.length === 0) return false;
  
  for (let idx = 0; idx < details.length; idx++) {
    const detail = details[idx];
    
    // Check if tracking data is provided based on tracking type
    if (detail.trackingType === 'batch' && (!detail.batches || detail.batches.length === 0)) {
      return false;
    }
    
    if (detail.trackingType === 'serial' && (!detail.serials || detail.serials.length === 0)) {
      return false;
    }
    
    // Check batch error for products that require batches
    if (detail.trackingType === 'batch' && detail.batchError) {
      return false;
    }
    
    // For products that don't require tracking, use stock validation
    if (detail.trackingType === 'none') {
      if (detail.isCalculatedQtyModified) {
        const isValid = stockValidation[idx]?.valid ?? true;
        const message = stockValidation[idx]?.message || '';
        
        // If negative inventory is not allowed and validation fails
        if (!companySettings.AllowNegativeInventory && !detail.allowProductNegativeInventory && !isValid) {
          return false;
        }
      }
    }
  }
  
  return true;
}, [details, stockValidation, companySettings]);
  
  // Get quantity validation errors
 const quantityErrors = useMemo(() => {
  const errs = [];
  
  details.forEach((detail, idx) => {
    // For batch products, check batch error
    if (detail.trackingType === 'batch') {
      if (detail.batchError) {
        errs.push(`Row ${idx + 1}: ${detail.batchError}`);
      }
    } else if (detail.trackingType === 'serial') {
      // Only show error if user has attempted to select serials
      if (detail.serialSelectionAttempted) {
        const requiredQty = parseFloat(detail.calculatedQty) || 0;
        if (detail.serials && detail.serials.length !== requiredQty) {
          errs.push(`Row ${idx + 1}: Number of serials (${detail.serials.length}) does not match quantity (${requiredQty})`);
        }
      }
    } else {
      // For non-tracking products, use stock validation
      if (detail.isCalculatedQtyModified) {
        const isValid = stockValidation[idx]?.valid ?? true;
        const message = stockValidation[idx]?.message || '';
        
        // If negative inventory is not allowed and validation fails
        if (!companySettings.AllowNegativeInventory && !detail.allowProductNegativeInventory && !isValid) {
          errs.push(`Row ${idx + 1}: ${message}`);
        }
      }
    }
  });
  
  return errs;
}, [details, stockValidation, companySettings]);
  
  // Function to recalculate details when header quantity changes
  const recalculateDetails = (newHeaderQty) => {
    if (details.length === 0) return;
    
    const newHeaderQtyNum = parseFloat(newHeaderQty) || 0;
    
    const updatedDetails = details.map(detail => {
      const detailQty = parseFloat(detail.qty) || 0;
      const newCalculatedQty = detailQty * newHeaderQtyNum;
      
      return {
        ...detail,
        calculatedQty: newCalculatedQty.toFixed(3).toString(),
        isCalculatedQtyModified: true // Mark as modified since it changed due to header qty change
      };
    });
    
    setDetails(updatedDetails);
    
    // Now, trigger validation for each detail
    updatedDetails.forEach((detail, idx) => {
      if (detail.trackingType === 'batch' && detail.batches && detail.batches.length > 0) {
        // For batch products, validate the batch quantity
        const validation = validateBatchQuantity(idx, detail);
        if (!validation.valid) {
          // Set error message
          const newDetails = [...updatedDetails];
          newDetails[idx] = { ...newDetails[idx], batchError: validation.message };
          setDetails(newDetails);
          alert(validation.message);
        }
      } else if (detail.trackingType === 'serial' && detail.serials && detail.serials.length > 0) {
        // For serial products, validate the number of serials
        const requiredQty = parseFloat(detail.calculatedQty) || 0;
        if (detail.serials.length !== requiredQty) {
          const newDetails = [...updatedDetails];
          newDetails[idx] = { 
            ...newDetails[idx], 
            serialError: `Number of serials (${detail.serials.length}) does not match quantity (${requiredQty})`
          };
          setDetails(newDetails);
          alert(newDetails[idx].serialError);
        }
      } else if (detail.productCode && detail.warehouse) {
        // For non-tracking products, validate using the API
        validateItemStock(
          detail.productCode,
          detail.warehouse,
          detail.calculatedQty,
          idx,
          true, // isQuantityModified: true because it changed
          detail.originalCalculatedQty,
          detail.allowProductNegativeInventory
        );
      }
    });
  };
  
  // Handle header changes
  const handleHeaderChange = async (e) => {
    const { name, value } = e.target;
    
    if (name === 'productionOrderId' && value) {
      console.log('Production order selected:', value);
      // Clear any previous API errors
      setApiError(null);
      // Update the header state with the selected production order ID
      setHeader(prev => ({ ...prev, [name]: value }));
      
      if (value) {
        await fetchProductionOrderDetails(value);
      } else {
        // Clear details and production order quantity if no production order is selected
        setProductionOrderDetails([]);
        setSelectedProductionOrder(null);
        setDetails([]);
        setHeader(prev => ({
          ...prev,
          productionOrderQty: '0'
        }));
        setOriginalProductionOrderQty(0);
      }
    } 
    // Handle production order quantity changes
    else if (name === 'productionOrderQty') {
      const newQty = parseFloat(value) || 0;
      setHeader(prev => ({ ...prev, [name]: value }));
      
      // Recalculate details if we have details and trigger validation
      if (details.length > 0) {
        recalculateDetails(value);
      }
    } 
    else {
      setHeader(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle detail changes
  const handleDetailChange = async (idx, e) => {
  const { name, value } = e.target;
  const newDetails = [...details];
  newDetails[idx][name] = value;
  
  // If calculated quantity is changed, mark it as modified and validate
  if (name === 'calculatedQty') {
    const originalCalculatedQty = parseFloat(newDetails[idx].originalCalculatedQty) || 0;
    const newCalculatedQty = parseFloat(value) || 0;
    
    // Mark as modified if changed
    newDetails[idx].isCalculatedQtyModified = originalCalculatedQty !== newCalculatedQty;
    
    // For non-tracking products, validate using the API
    if (newDetails[idx].trackingType === 'none') {
      if (newDetails[idx].productCode && newDetails[idx].warehouse) {
        // Check if negative inventory is allowed at company or product level
        if (!companySettings.AllowNegativeInventory && !newDetails[idx].allowProductNegativeInventory) {
          // Only validate if negative inventory is not allowed
          validateItemStock(
            newDetails[idx].productCode,
            newDetails[idx].warehouse,
            value,
            idx,
            newDetails[idx].isCalculatedQtyModified,
            newDetails[idx].originalCalculatedQty,
            newDetails[idx].allowProductNegativeInventory
          );
        } else {
          // If negative inventory is allowed, clear any previous validation error
          setStockValidation(prev => ({
            ...prev,
            [idx]: { valid: true, message: 'Negative inventory allowed' }
          }));
        }
      }
    }
    
    // ========== BATCH PRODUCT VALIDATION ==========
    if (newDetails[idx].trackingType === 'batch' && newDetails[idx].batches && newDetails[idx].batches.length > 0) {
      const validation = validateBatchQuantity(idx, newDetails[idx]);
      
      // If negative inventory is NOT allowed and validation fails
      if (!companySettings.AllowNegativeInventory || !newDetails[idx].allowProductNegativeInventory) {
        if (!validation.valid) {
          // Adjust quantity to original + available quantity
          newDetails[idx].calculatedQty = (originalCalculatedQty + validation.availableQuantity).toString();
          newDetails[idx].batchError = validation.message;
          
          // Show a notification to the user
          alert(validation.message);
        } else {
          // Clear any previous batch error
          newDetails[idx].batchError = '';
        }
      }
    } 
    // If product requires serial tracking but none selected
    else if (newDetails[idx].trackingType === 'serial' && (!newDetails[idx].serials || newDetails[idx].serials.length === 0)) {
      newDetails[idx].serialError = 'Please select serials first';
    }
  }

  if (name === 'rate') {
    const qty = parseFloat(newDetails[idx].calculatedQty) || 0;
    const rateValue = parseFloat(value) || 0;
    const calculatedValue = (qty * rateValue).toFixed(2);
    newDetails[idx].value = calculatedValue;
  }
  
  // If warehouse changes and product allows batch, reset batch fields
  if (name === 'warehouse' && newDetails[idx].trackingType === 'batch') {
    newDetails[idx].batchNumber = '';
    newDetails[idx].expiryDate = '';
    newDetails[idx].availableBatches = [];
    newDetails[idx].batchError = '';
    newDetails[idx].batchQuantity = 0;
  }
  
  setDetails(newDetails);
};
  
  // Fetch production order details
  const fetchProductionOrderDetails = async (productionOrderId) => {
    setLoadingProductionOrderDetails(true);
    setApiError(null);
    try {
      const token = localStorage.getItem('authToken');
      console.log('Fetching production order details for:', productionOrderId);
      
      // Check if token exists
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const url = `${API_URL}/issue-for-production/production-order/${productionOrderId}`;
      console.log('Making request to:', url);
      
      const response = await axios.get(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10 seconds timeout
      });
      
      const data = response.data;
      console.log('Production order details response:', data);
      
      if (!data.productionOrder) {
        console.error('Production order not found in response:', productionOrderId);
        setApiError(`Production order ${productionOrderId} not found`);
        setProductionOrderDetails([]);
        setSelectedProductionOrder(null);
        return;
      }
      
      // Store the original production order quantity
      const originalQty = parseFloat(data.productionOrder.Qty) || 0;
      setOriginalProductionOrderQty(originalQty);
      console.log('Original production order quantity:', originalQty);
      
      // Update header with production order quantity (only in create mode)
      if (!isEdit) {
        setHeader(prev => ({
          ...prev,
          productionOrderQty: originalQty.toString()
        }));
        console.log('Header updated with production order quantity:', originalQty);
      }
      
      setProductionOrderDetails(data.details || []);
      setSelectedProductionOrder(data.productionOrder || null);
      
      // If we're in create mode, auto-fill the details
      if (!isEdit) {
        // If dropdownData.products is empty, fetch product details for each product
        let products = dropdownData.products;
        if (products.length === 0) {
          console.log('Products not available in dropdownData, fetching...');
          try {
            const productsResponse = await axios.get(`${API_URL}/products`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            products = productsResponse.data;
            console.log('Fetched products:', products);
            // Update dropdownData with the fetched products
            setDropdownData(prev => ({ ...prev, products }));
          } catch (error) {
            console.error('Error fetching products:', error);
            // Don't throw here, we'll continue with empty products array
          }
        }
        
        const newDetails = (data.details || []).map(detail => {
          // Find product details
          const productDetails = products.find(p => p.ProductCode === detail.ProductCode);
          console.log(`Product details for ${detail.ProductCode}:`, productDetails);
          
          // Calculate the calculated quantity based on the production order quantity and detail qty
          const calculatedQty = (parseFloat(detail.Qty) || 0) * originalQty;
          
          // Determine tracking type based on product settings
          let trackingType = 'none';
          if (productDetails) {
            if (productDetails.AllowProductTracking === 1 || productDetails.AllowProductTracking === 'batch') {
              trackingType = 'batch';
            } else if (productDetails.AllowProductTracking === 2 || productDetails.AllowProductTracking === 'serial') {
              trackingType = 'serial';
            }
          }
          
          return {
            productionOrderDetailId: detail.ProductionOrderDetailID || '',
            materialType: detail.MaterialType || '',
            productCode: detail.ProductCode || '',
            productName: detail.ProductName || '',
            qty: detail.Qty?.toString() || '',
            calculatedQty: calculatedQty.toFixed(3).toString(),
            originalCalculatedQty: calculatedQty.toFixed(3).toString(),
            rate: '',  // Add this
            value: '',   // Add this
            flushType: detail.FlushType || '',
            uom: detail.UOM || '',
            warehouse: '', // Start with empty warehouse
            batchNumber: '',
            expiryDate: '',
            showBatchPopup: false,
            availableBatches: [],
            batchError: '',
            originalBatchNumber: '',
            originalExpiryDate: '',
            isBatchModified: false,
            allowBatchOnProduct: productDetails ? productDetails.AllowBatchOnProduct : false,
            allowProductNegativeInventory: productDetails ? productDetails.AllowProductNegativeInventory : false,
            batchQuantity: 0,
            department_id: "", // Add department_id
            trackingType: trackingType, // Add tracking type
            batches: [], // Initialize empty batches array
            serials: [], // Initialize empty serials array
            showBatchSelection: false,
            showSerialSelection: false
          };
        });
        
        console.log('New details to be set:', newDetails);
        setDetails(newDetails);
      }
    } catch (error) {
      console.error('Error fetching production order details:', error);
      
      // Set detailed error message
      let errorMessage = 'Error fetching production order details';
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
        
        if (error.response.status === 404) {
          errorMessage = `Production order ${productionOrderId} not found`;
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to access this production order.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
          if (error.response.data && error.response.data.message) {
            errorMessage += ` Details: ${error.response.data.message}`;
          }
        } else {
          errorMessage = `Server returned error ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your network connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
        errorMessage = `Error: ${error.message}`;
      }
      
      setApiError(errorMessage);
      setProductionOrderDetails([]);
      setSelectedProductionOrder(null);
    } finally {
      setLoadingProductionOrderDetails(false);
    }
  };
  
  // Initialize form when opening
  useEffect(() => {
    if (open) {
      // Reset form state when opening
      setErrors({});
      setDropdownError(null);
      setOriginalProductionOrderQty(0); // Reset original quantity
      setApiError(null); // Reset API error
      setStockValidation({}); // Reset stock validation
      
      fetchCurrentUser();
      fetchCompanySettings();
      fetchScreenConfig(); // Add this line
      
  const initializeForm = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentUser = {
      UserID: payload.userId,
      UserName: payload.email
    };
    
    try {
      const response = await axios.get(`${API_URL}/users/${currentUser.UserID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      currentUser.UserName = response.data.Username || response.data.FullName || response.data.Email;
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    
    // Wait for dropdown data to be fetched
    const data = await fetchDropdownData();
    
    if (isEdit && initialData) {
      // Set header with initial data
      setHeader({
        issueId: initialData.IssueID || '',
        issueDate: initialData.IssueDate ? new Date(initialData.IssueDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        productionOrderId: initialData.ProductionOrderID || '',
        productionOrderQty: initialData.ProductionOrderQty?.toString() || '0',
        branchCode: initialData.BranchCode || '',
        issuedByUserId: initialData.IssuedByUserID?.toString() || '',
        issuedByUserName: initialData.IssuedByUserName || '',
        inventoryFlag: initialData.InventoryFlag ? 'yes' : 'no',
        department_id: initialData.DepartmentID?.toString() || "",
      });
      
      // Fetch production order details to get original quantity
      if (initialData.ProductionOrderID) {
        await fetchProductionOrderDetails(initialData.ProductionOrderID);
        
        // After fetching, restore the header quantity from initial data
        setHeader(prev => ({
          ...prev,
          productionOrderQty: initialData.ProductionOrderQty?.toString() || '0'
        }));
      }
      
      if (initialData.Details && initialData.Details.length > 0) {
        const productCodes = [...new Set(initialData.Details.map(detail => detail.ProductCode))];
        const productPromises = productCodes.map(async (productCode) => {
          try {
            // First check if product is already in dropdownData
            const existingProduct = data.products.find(p => p.ProductCode === productCode);
            if (existingProduct) return existingProduct;
            
            // If not found, fetch it
            const response = await axios.get(`${API_URL}/products/${productCode}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data;
          } catch (error) {
            console.error(`Error fetching product ${productCode}:`, error);
            return null;
          }
        });

        const productDetailsArray = await Promise.all(productPromises);
        const productDetailsMap = {};
        productDetailsArray.forEach(product => {
          if (product) {
            productDetailsMap[product.ProductCode] = product;
          }
        });
        
        // Create a promise to fetch tracking information for all items
        const trackingPromises = initialData.Details.map(async (detail) => {
          try {
            const trackingData = await fetchTrackingData(initialData.IssueID, detail.IssueDetailID);
            return { ...detail, trackingData };
          } catch (error) {
            console.error(`Error fetching tracking data for detail ${detail.IssueDetailID}:`, error);
            return { ...detail, trackingData: [] };
          }
        });

        // Wait for all tracking data to be fetched
        const detailsWithTracking = await Promise.all(trackingPromises);
        
        // Create a promise to fetch batch information for all items that require batches
        const batchPromises = detailsWithTracking.map(async (detail) => {
          // Default values
          let batchQuantity = Math.abs(detail.CalculatedQty) || 0;
          let availableBatches = [];
          
          // If the product allows batches and a batch number is provided, fetch batch information
          if (detail.BatchNumber && detail.Warehouse) {
            try {
              const response = await axios.get(`${API_URL}/products/${detail.ProductCode}/batches`, {
                params: { warehouseCode: detail.Warehouse },
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              availableBatches = response.data;
              // Find the specific batch that matches the item's batch number and expiry date
              const matchedBatch = availableBatches.find(batch => 
                batch.BatchNumber === detail.BatchNumber && 
                (detail.ExpiryDate ? 
                  (batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] === new Date(detail.ExpiryDate).toISOString().split('T')[0] : false) 
                  : !batch.ExpiryDate)
              );
              
              // If we found a matching batch, use its quantity
              if (matchedBatch) {
                batchQuantity = matchedBatch.TotalQuantity || 0;
              }
            } catch (error) {
              console.error('Error fetching batch information:', error);
            }
          }
          
          return {
            ...detail,
            availableBatches,
            batchQuantity
          };
        });
        
        // Wait for all batch information to be fetched
        const detailsWithBatchInfo = await Promise.all(batchPromises);
        
        // Now map the details with batch and tracking information
        const newDetails = detailsWithBatchInfo.map(detail => {
          // Find product details
          const productDetails = productDetailsMap[detail.ProductCode];
          
          // Determine tracking type
          let trackingType = 'none';
          if (productDetails) {
            if (productDetails.AllowProductTracking === 1 || productDetails.AllowProductTracking === 'batch') {
              trackingType = 'batch';
            } else if (productDetails.AllowProductTracking === 2 || productDetails.AllowProductTracking === 'serial') {
              trackingType = 'serial';
            }
          }
          
          // Process tracking data to get batches and serials
          const batches = detail.trackingData
            .filter(item => item.TrackingType === 'batch')
            .map(item => ({
              batchNumber: item.TrackingNumber,
              quantity: item.Quantity,
              expiryDate: item.ExpiryDate ? new Date(item.ExpiryDate).toISOString().split('T')[0] : ''
            }));

          const serials = detail.trackingData
            .filter(item => item.TrackingType === 'serial')
            .map(item => ({
              SerialNumber: item.TrackingNumber
            }));
          
          return {
            productionOrderDetailId: detail.ProductionOrderDetailID || '',
            materialType: detail.MaterialType || '',
            productCode: detail.ProductCode || '',
            productName: detail.ProductName || '',
            qty: detail.Qty?.toString() || '',
            calculatedQty: detail.CalculatedQty?.toString() || '',
            rate: detail.Rate?.toString() || '0',
            value: detail.Value?.toString() || '0',
            originalCalculatedQty: detail.CalculatedQty?.toString() || '',
            flushType: detail.FlushType || '',
            uom: detail.UOM || '',
            warehouse: detail.Warehouse || '',
            batchNumber: detail.BatchNumber || '',
            expiryDate: detail.ExpiryDate ? new Date(detail.ExpiryDate).toISOString().split('T')[0] : '',
            showBatchPopup: false,
            availableBatches: detail.availableBatches || [],
            batchError: '',
            originalBatchNumber: detail.BatchNumber || '',
            originalExpiryDate: detail.ExpiryDate ? new Date(detail.ExpiryDate).toISOString().split('T')[0] : '',
            isBatchModified: false,
            allowBatchOnProduct: productDetails ? productDetails.AllowBatchOnProduct : false,
            allowProductNegativeInventory: productDetails ? productDetails.AllowProductNegativeInventory : false,
            batchQuantity: detail.batchQuantity || 0,
            department_id: detail.DepartmentID?.toString() || "",
            trackingType: trackingType,
            batches: batches, // Use processed tracking data
            serials: serials, // Use processed tracking data
            availableSerials: detail.availableSerials || [],
            showBatchSelection: false,
            showSerialSelection: false
          };
        });
        
        setDetails(newDetails);
      } else {
        setDetails([]);
      }
      
      // If editing, fetch the production order details
      if (initialData.ProductionOrderID) {
        fetchProductionOrderDetails(initialData.ProductionOrderID);
      }
    } else {
      // For create mode, preserve the issueId that was set in fetchDropdownData
      setHeader({
        ...initialHeader,
        issueId: data.nextIssueNumber || '',
        issuedByUserId: currentUser.UserID.toString(),
        issuedByUserName: currentUser.UserName || ''
      });
      
      // If dropdownData.products is empty, fetch product details for each product
      let products = dropdownData.products;
      if (products.length === 0) {
        console.log('Products not available in dropdownData, fetching...');
        try {
          const productsResponse = await axios.get(`${API_URL}/products`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          products = productsResponse.data;
          console.log('Fetched products:', products);
          // Update dropdownData with the fetched products
          setDropdownData(prev => ({ ...prev, products }));
        } catch (error) {
          console.error('Error fetching products:', error);
          // Don't throw here, we'll continue with empty products array
        }
      }
      
      // Initialize with empty details array
      setDetails([]);
    }
    
    setIsSubmitting(false);
  } catch (error) {
    console.error('Error initializing form:', error);
    alert('Error initializing form. Please try again.');
    // Don't close the modal on error
  } finally {
    setLoading(false);
  }
};
      
      initializeForm();
    } else {
      // Reset state when closing
      setErrors({});
      setDropdownError(null);
      setOriginalProductionOrderQty(0);
      setApiError(null);
      setStockValidation({});
    }
  }, [open, initialData, isEdit, onClose]);
  
const fetchDropdownData = async () => {
  try {
    setLoading(true);
    setDropdownError(null);
    
    // Fetch data from existing endpoints
    const [
      usersResponse,
      productionOrdersResponse,
      warehousesResponse,
      branchesResponse,
      nextIssueNumberResponse,
      nextDetailNumberResponse,
      productsResponse,
      companySettingsResponse,
      screenConfigResponse
    ] = await Promise.all([
      axios.get(`${API_URL}/user`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/production-order`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/branches`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/issue-for-production/next-issue-number`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/issue-for-production/next-detail-number`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/products`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/company/settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      }),
      axios.get(`${API_URL}/screens`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      })
    ]);
    
    // Process screen configuration
    const screensData = screenConfigResponse.data;
    const issueForProductionScreen = screensData.screens?.find(screen => 
      screen.ScreenName.toLowerCase() === 'IssueForProduction'.toLowerCase()
    );
    
    const data = {
      users: usersResponse.data || [],
      productionOrders: productionOrdersResponse.data || [],
      warehouses: warehousesResponse.data || [],
      branches: branchesResponse.data || [],
      nextIssueNumber: nextIssueNumberResponse.data.nextIssueNumber || '',
      nextDetailNumber: nextDetailNumberResponse.data.nextDetailNumber || '',
      products: productsResponse.data || [],
      companySettings: companySettingsResponse.data || { AllowNegativeInventory: false, AllowWarehouseNegativeInventory: false },
      screenConfig: issueForProductionScreen || null
    };
    
    console.log('Processed dropdown data:', data); // Debug log
    
    setDropdownData(data);
    setCompanySettings(data.companySettings);
    
    if (issueForProductionScreen) {
      setScreenConfig(issueForProductionScreen);
      // Show department field if ShowOnHeader is true
      const shouldShowDepartment = issueForProductionScreen.ShowOnHeader === true;
      setShowDepartmentField(shouldShowDepartment);
      
      // Show department item field if ShowOnItem is true
      const shouldShowDepartmentItem = issueForProductionScreen.ShowOnItem === true;
      setShowDepartmentItemField(shouldShowDepartmentItem);
      
      // If department fields should be shown, fetch departments
      if (shouldShowDepartment || shouldShowDepartmentItem) {
        fetchDepartments();
      }
    }
    
    if (!isEdit) {
      setHeader(prev => ({ 
        ...prev, 
        issueId: data.nextIssueNumber || ''
      }));
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    setDropdownError('Error loading form data: ' + (error.response?.data?.message || error.message));
    alert('Error loading form data');
    // Return empty data to avoid breaking the UI
    return {
      users: [],
      productionOrders: [],
      warehouses: [],
      branches: [],
      nextIssueNumber: '',
      nextDetailNumber: '',
      products: [],
      companySettings: { AllowNegativeInventory: false, AllowWarehouseNegativeInventory: false },
      screenConfig: null
    };
  } finally {
    setLoading(false);
  }
};
  
  const validate = () => {
    const errs = {};
    if (!header.issueId || !header.issueId.trim()) errs.issueId = 'Required';
    if (!header.issueDate) errs.issueDate = 'Required';
    if (!header.productionOrderId) errs.productionOrderId = 'Required';
    if (!header.issuedByUserId) errs.issuedByUserId = 'Required';
    if (!header.issuedByUserName) errs.issuedByUserName = 'Required';
    
    // Validate department field only if it's shown
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    if (details.length === 0) {
      errs.details = 'At least one detail is required';
    } else {
      details.forEach((detail, idx) => {
        if (!detail.productCode) errs[`productCode_${idx}`] = 'Required';
        if (!detail.productName) errs[`productName_${idx}`] = 'Required';
        if (!detail.uom) errs[`uom_${idx}`] = 'Required';
        if (!detail.calculatedQty) errs[`calculatedQty_${idx}`] = 'Required';
        if (!detail.warehouse) errs[`warehouse_${idx}`] = 'Required';
        
        // Check if tracking data is provided based on tracking type
        if (detail.trackingType === 'batch' && (!detail.batches || detail.batches.length === 0)) {
          errs[`batches_${idx}`] = 'Batch selection is required for this product';
        }
        
        if (detail.trackingType === 'serial' && (!detail.serials || detail.serials.length === 0)) {
          errs[`serials_${idx}`] = 'Serial selection is required for this product';
        }
        
        // Validate department item field only if it's shown
        if (showDepartmentItemField && !detail.department_id) errs[`department_id_${idx}`] = 'Required';
      });
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
 const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Check for quantity validation errors
  const hasQuantityErrors = details.some((detail, idx) => {
    if (detail.trackingType === 'batch') {
      return !!detail.batchError;
    } else if (detail.trackingType === 'serial') {
      return !!detail.serialError;
    } else if (detail.trackingType === 'none') {
      // Check stock validation for non-tracking products
      const validation = stockValidation[idx];
      // Only consider it an error if negative inventory is not allowed and validation failed
      return validation && !validation.valid && 
             !companySettings.AllowNegativeInventory && 
             !detail.allowProductNegativeInventory;
    }
    return false;
  });
  
  if (hasQuantityErrors) {
    alert('Please fix the quantity validation errors before submitting.');
    return;
  }
  
  if (!validate() || isSubmitting) return;
  
  if (!isUserValid || !currentUser || !currentUser.UserID) {
    alert(authError || 'User information not available. Please refresh the page and log in again.');
    return;
  }
  
  if (quantityErrors.length > 0) {
    alert('Please fix the quantity errors before submitting.');
    return;
  }
  
  setIsSubmitting(true);
  setLoading(true);
  
  try {
    const payload = {
      IssueID: header.issueId,
      ProductionOrderID: header.productionOrderId,
      ProductionOrderQty: parseFloat(header.productionOrderQty) || 0,
      IssueDate: header.issueDate,
      InventoryFlag: header.inventoryFlag === 'yes',
      BranchCode: header.branchCode,
      IssuedByUserID: header.issuedByUserId,
      IssuedByUserName: header.issuedByUserName,
      // Only include department_id if showDepartmentField is true
      ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
      ...(isEdit ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
      Details: details.map(detail => {
        // Create the detail object with tracking data
        const detailPayload = {
          ProductionOrderDetailID: detail.productionOrderDetailId,
          MaterialType: detail.materialType,
          ProductCode: detail.productCode,
          ProductName: detail.productName,
          Qty: parseFloat(detail.qty) || 0,
          CalculatedQty: parseFloat(detail.calculatedQty) || 0,
          Rate: parseFloat(detail.rate) || 0,    // Add this
          Value: parseFloat(detail.value) || 0 ,  // Add this
          FlushType: detail.flushType,
          UOM: detail.uom,
          Warehouse: detail.warehouse,
          // Only include department_id if showDepartmentItemField is true
          ...(showDepartmentItemField && detail.department_id ? { department_id: parseInt(detail.department_id) } : {})
        };
        
        // Add tracking data based on tracking type
        if (detail.trackingType === 'batch' && detail.batches && detail.batches.length > 0) {
          detailPayload.Batches = detail.batches.map(batch => ({
            BatchNumber: batch.batchNumber,
            Quantity: batch.quantity,
            ExpiryDate: batch.expiryDate || null
          }));
        }
        
        if (detail.trackingType === 'serial' && detail.serials && detail.serials.length > 0) {
          detailPayload.Serials = detail.serials.map(serial => ({
            SerialNumber: serial.SerialNumber
          }));
        }
        
        return detailPayload;
      })
    };
    
    const url = isEdit 
      ? `${API_URL}/issue-for-production/${header.issueId}`
      : `${API_URL}/issue-for-production`;
    
    const method = isEdit ? 'PUT' : 'POST';
    
    const response = await axios({
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      data: payload
    });
    
    if (onSubmit) {
      onSubmit();
    }
    
    onClose();
  } catch (err) {
    console.error('Error saving issue for production record:', err);
    alert(err.response?.data?.message || 'Error saving issue for production record');
  } finally {
    setIsSubmitting(false);
    setLoading(false);
  }
};

  // Add this function inside the IssueForProductionFormModal component
const fetchTrackingData = async (issueId, issueDetailId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_URL}/issue-for-production/${issueId}/item/${issueDetailId}/tracking`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching tracking data:', error);
    return [];
  }
};
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .quantity-exceeded {
            animation: shake 0.5s;
            border-color: #ef4444 !important;
            background-color: #fee2e2 !important;
          }
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
          }
          .modal-transition {
            transition: all 0.3s ease-in-out;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Issue For Production' : 'Create Issue For Production'}</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content - only show if not minimized */}
        {!isMinimized && (
          <>
            {/* User validation warning */}
            {open && !isUserValid && (
              <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {authError || 'User information not available. Please refresh the page and log in again.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Dropdown error message */}
            {dropdownError && (
              <div className="mx-8 mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      {dropdownError}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* API error message */}
            {apiError && (
              <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {apiError}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Scrollable Content Area */}
            <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
              <form onSubmit={handleSubmit} className="space-y-6 pb-6">
                {/* IssueForProduction Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Issue For Production Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Issue ID *</label>
                      <input 
                        name="issueId" 
                        value={header.issueId} 
                        onChange={handleHeaderChange} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                      {errors.issueId && <span className="text-xs text-red-500">{errors.issueId}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Issue Date *</label>
                      <input 
                        type="date" 
                        name="issueDate" 
                        value={header.issueDate} 
                        onChange={handleHeaderChange} 
                        className={`w-full border rounded-lg px-3 py-2 ${errors.issueDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                      />
                      {errors.issueDate && <span className="text-xs text-red-500">{errors.issueDate}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Production Order ID *</label>
                      {loading ? (
                        <div className="text-gray-500 text-sm">Loading production orders...</div>
                      ) : dropdownError ? (
                        <div className="text-red-500 text-sm">{dropdownError}</div>
                      ) : (
                        <SearchableDropdown
  options={dropdownData.productionOrders || []}
  value={header.productionOrderId}
  onChange={(productionOrderId) => {
    handleHeaderChange({
      target: { name: 'productionOrderId', value: productionOrderId }
    });
  }}
  getOptionLabel={(order) => `${order.ProductionOrderID} - ${order.ProductName}`}
  getOptionValue={(order) => order.ProductionOrderID}
  getOptionSecondaryLabel={(order) => order.ProductName}
  placeholder="Select Production Order"
  error={errors.productionOrderId}
  disabled={isEdit || loading}
  className="uppercase"
/>
                      )}
                      {errors.productionOrderId && <span className="text-xs text-red-500">{errors.productionOrderId}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Production Order Qty</label>
                      <input 
                        name="productionOrderQty" 
                        type="number"
                        min="0"
                        step="0.001"
                        value={header.productionOrderQty} 
                        onChange={handleHeaderChange} 
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Original: {originalProductionOrderQty}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Issued By User ID *</label>
                      <input 
                        name="issuedByUserId" 
                        value={header.issuedByUserId} 
                        onChange={handleHeaderChange}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                        title="Auto-populated from logged-in user"
                      />
                      {errors.issuedByUserId && <span className="text-xs text-red-500">{errors.issuedByUserId}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Issued By User Name *</label>
                      <input 
                        name="issuedByUserName" 
                        value={header.issuedByUserName} 
                        onChange={handleHeaderChange}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        title="Auto-populated from logged-in user"
                      />
                      {errors.issuedByUserName && <span className="text-xs text-red-500">{errors.issuedByUserName}</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                     <SearchableDropdown
  options={dropdownData.branches || []}
  value={header.branchCode}
  onChange={(branchCode) => {
    handleHeaderChange({
      target: { name: 'branchCode', value: branchCode }
    });
  }}
  getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
  getOptionValue={(branch) => branch.BranchCode}
  getOptionSecondaryLabel={(branch) => branch.BranchName}
  placeholder="Select Branch"
/>
                    </div>
                    
                    {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                    {showDepartmentField && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">
                          Department *
                        </label>
                       <SearchableDropdown
  options={departments}
  value={header.department_id}
  onChange={(department_id) => {
    handleHeaderChange({
      target: { name: 'department_id', value: department_id }
    });
  }}
  getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
  getOptionValue={(dept) => dept.DepartmentID.toString()}
  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
  placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
  error={errors.department_id}
  disabled={loadingDepartments}
  className="uppercase"
/>
                        {errors.department_id && (
                          <span className="text-xs text-red-500">
                            {errors.department_id}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Update Inventory *</label>
                      <select 
                        name="inventoryFlag" 
                        value={header.inventoryFlag} 
                        onChange={handleHeaderChange} 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      >
                        {INVENTORY_FLAG_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Select "Yes" to update inventory, "No" to skip inventory update</p>
                    </div>
                  </div>
                </div>
                
                {/* IssueForProduction Details */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">Issue For Production Details</h3>
                  </div>
                  
                  {errors.details && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
                      {errors.details}
                    </div>
                  )}
                  
                  {loadingProductionOrderDetails ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Loading production order details...</p>
                    </div>
                  ) : details.length > 0 ? (
                    <>
                      <div className="overflow-x-auto custom-scrollbar">
                        <table className="min-w-full border border-[#E2E8F0]">
                          <thead>
                            <tr className="bg-[#BDDDFC] text-[#384959]">
                              <th className="p-2">#</th>
                              <th className="p-2">Product Name</th>
                              <th className="p-2">Product Code</th>
                              <th className="p-2">Material Type</th>
                              <th className="p-2">UOM</th>
                              <th className="p-2">Qty</th>
                              <th className="p-2">Calculated Qty</th>
                              <th className="p-2">Rate</th>
                              <th className="p-2">Value</th>
                              <th className="p-2">Flush Type</th>
                              <th className="p-2">Warehouse</th>
                              {/* Department column - Only shown if ShowOnItem is true */}
                              {showDepartmentItemField && <th className="p-2">Department</th>}
                              <th className="p-2">Tracking Type</th>
                              <th className="p-2">Tracking Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.map((detail, idx) => (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <input 
                                    name="productName" 
                                    value={detail.productName} 
                                    onChange={e => handleDetailChange(idx, e)} 
                                    readOnly
                                    className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                  />
                                  {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="productCode" 
                                    value={detail.productCode} 
                                    onChange={e => handleDetailChange(idx, e)} 
                                    readOnly
                                    className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                  />
                                  {errors[`productCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="materialType" 
                                    value={detail.materialType} 
                                    onChange={e => handleDetailChange(idx, e)} 
                                    readOnly
                                    className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="uom" 
                                    value={detail.uom} 
                                    readOnly 
                                    className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  />
                                  {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="qty" 
                                    value={detail.qty} 
                                    readOnly
                                    className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                  />
                                </td>
                               
<td className="p-2">
  <div className="flex flex-col">
    <input 
      name="calculatedQty" 
      type="number" 
      min="0" 
      step="0.001"
      value={detail.calculatedQty} 
      onChange={e => handleDetailChange(idx, e)} 
      className={`w-20 border rounded-lg px-2 py-1 ${
        (detail.isCalculatedQtyModified && 
         !companySettings.AllowNegativeInventory && 
         !detail.allowProductNegativeInventory && 
         stockValidation[idx]?.valid === false) || 
        (detail.isCalculatedQtyModified && quantityErrors.some(err => err.includes(`Row ${idx + 1}`))) ||
        (detail.batchError && detail.trackingType === 'batch') ||
        (detail.serialError && detail.trackingType === 'serial')
          ? 'quantity-exceeded border-red-400 bg-red-50' 
          : 'border-[#E2E8F0]'
      } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
    />
    
    {/* Show stock validation status for non-tracking products */}
    {detail.trackingType === 'none' && detail.isCalculatedQtyModified && (
      <div className="mt-1">
        {stockValidation[idx] && (
          <span className={`text-xs ${
            stockValidation[idx].valid ? 'text-green-600' : 'text-red-500'
          }`}>
            {stockValidation[idx].message}
          </span>
        )}
        
        {/* Show negative inventory status */}
        {companySettings.AllowNegativeInventory && detail.allowProductNegativeInventory && (
          <span className="text-xs text-green-600">
            Negative inventory allowed
          </span>
        )}
      </div>
    )}
    
    {detail.batchError && detail.trackingType === 'batch' && (
      <span className="text-xs text-red-500 mt-1">
        {detail.batchError}
      </span>
    )}
    {detail.serialError && detail.trackingType === 'serial' && (
      <span className="text-xs text-red-500 mt-1">
        {detail.serialError}
      </span>
    )}
    {isEdit && !detail.isCalculatedQtyModified && (
      <span className="text-xs text-green-600 mt-1">
        Original: {detail.originalCalculatedQty}
      </span>
    )}
  </div>
</td>
                                <td className="p-2">
                                  <input 
                                    name="rate"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={detail.rate}
                                    onChange={e => handleDetailChange(idx, e)}
                                    className="w-20 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="value"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={detail.value}
                                    readOnly
                                    className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    name="flushType" 
                                    value={detail.flushType} 
                                    readOnly
                                    className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  />
                                </td>
                                <td className="p-2">
                                  <SearchableDropdown
  options={dropdownData.warehouses || []}
  value={detail.warehouse}
  onChange={(warehouse) => {
    const newDetails = [...details];
    newDetails[idx].warehouse = warehouse;
    
    // If warehouse changes and product allows batch, reset batch fields
    if (newDetails[idx].trackingType === 'batch') {
      newDetails[idx].batchNumber = '';
      newDetails[idx].expiryDate = '';
      newDetails[idx].availableBatches = [];
      newDetails[idx].batchError = '';
      newDetails[idx].batchQuantity = 0;
    }
    
    setDetails(newDetails);
  }}
  getOptionLabel={(warehouse) => warehouse.LocationName}
  getOptionValue={(warehouse) => warehouse.LocationCode}
  getOptionSecondaryLabel={(warehouse) => warehouse.LocationCode}
  placeholder="Select Warehouse"
  width="w-24"
  error={errors[`warehouse_${idx}`]}
  className="uppercase"
/>
                                  {errors[`warehouse_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                                
                                {/* Department cell - Only shown if ShowOnItem is true */}
                                {showDepartmentItemField && (
                                  <td className="p-2">
                                   <SearchableDropdown
  options={departments}
  value={detail.department_id}
  onChange={(department_id) => {
    const newDetails = [...details];
    newDetails[idx].department_id = department_id;
    setDetails(newDetails);
  }}
  getOptionLabel={(dept) => dept.DepartmentCode}
  getOptionValue={(dept) => dept.DepartmentID.toString()}
  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
  placeholder="Select"
  width="w-24"
  error={errors[`department_id_${idx}`]}
  className="uppercase"
/>
                                    {errors[`department_id_${idx}`] && (
                                      <span className="text-xs text-red-500">*</span>
                                    )}
                                  </td>
                                )}
                                
                                <td className="p-2">
                                  <span className="text-sm font-medium">
                                    {detail.trackingType === 'batch' ? 'Batch' : 
                                     detail.trackingType === 'serial' ? 'Serial' : 'None'}
                                  </span>
                                </td>
                                
                                <td className="p-2">
                                  {detail.trackingType === 'batch' ? (
                                    <div>
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => handleBatchFieldClick(idx)}
                                      >
                                        {detail.batches && detail.batches.length > 0 
                                          ? `${detail.batches.length} batch(es)` 
                                          : 'Select Batches'}
                                      </button>
                                      {errors[`batches_${idx}`] && (
                                        <span className="text-xs text-red-500 block">{errors[`batches_${idx}`]}</span>
                                      )}
                                    </div>
                                  ) : detail.trackingType === 'serial' ? (
                                    <div>
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => handleSerialFieldClick(idx)}
                                      >
                                        {detail.serials && detail.serials.length > 0 
                                          ? `${detail.serials.length} serial(s)` 
                                          : 'Select Serials'}
                                      </button>
                                      {errors[`serials_${idx}`] && (
                                        <span className="text-xs text-red-500 block">{errors[`serials_${idx}`]}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">N/A</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {quantityErrors.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">
                          <h4 className="font-medium mb-2">Quantity Errors:</h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {quantityErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No details available. Select a production order to load details.</p>
                    </div>
                  )}
                  
                  {details.length > 0 && (
                    <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                      <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">Total Qty</span>
                          <span className="text-base font-semibold">{summary.totalQty}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">Total Calculated Qty</span>
                          <span className="text-base font-semibold text-lg text-blue-600">{summary.totalCalculatedQty}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">Production Order Qty</span>
                          <span className="text-base font-semibold text-lg text-green-600">{header.productionOrderQty}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
            
            {/* Fixed Footer with Buttons */}
            <div className="flex-shrink-0 p-8 pt-0">
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={loading || isSubmitting || !isFormValid || !isUserValid || details.length === 0}
                  onClick={handleSubmit}
                >
                  {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Issue For Production' : 'Save Issue For Production')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
   {showBatchSelection && selectedDetailIndex !== null && (
  <BatchSelectionPopup 
    open={showBatchSelection}
    onClose={() => setShowBatchSelection(false)}
    batchData={details[selectedDetailIndex].availableBatches}
    onSelectBatch={handleBatchSelect}
    productSettings={{
      allowNegativeInventory: details[selectedDetailIndex].allowProductNegativeInventory
    }}
    companySettings={companySettings}
    requiredQuantity={parseFloat(details[selectedDetailIndex].calculatedQty) || 0}
    selectedBatches={details[selectedDetailIndex].batches} // Pass current selected batches
    productCode={details[selectedDetailIndex].productCode}
    warehouseCode={details[selectedDetailIndex].warehouse}
    warehouseFiltering={{
      enabled: companySettings.AllowWarehouseNegativeInventory,
      applied: true,
      warehouseCode: details[selectedDetailIndex].warehouse
    }}
  />
)}
      
      {/* Serial Selection Popup */}
     {showSerialSelection && selectedDetailIndex !== null && (
  <SerialSelectionPopup 
    serials={details[selectedDetailIndex].availableSerials || []}
    onSelect={handleSerialSelect}
      onClose={handleSerialPopupClose} // Use the new handler
    warehouseWise={companySettings.AllowWarehouseNegativeInventory}
    warehouseName={dropdownData.warehouses.find(w => w.LocationCode === details[selectedDetailIndex].warehouse)?.LocationName}
    selectedSerials={details[selectedDetailIndex].serials || []}
    requiredQuantity={parseFloat(details[selectedDetailIndex].calculatedQty) || 0}
    allowNegative={Boolean(companySettings.AllowNegativeInventory)}
    productAllowNegative={Boolean(details[selectedDetailIndex].allowProductNegativeInventory)}
    isEdit={isEdit}
    productCode={details[selectedDetailIndex].productCode}
  />
)}
    </div>
  );
};

export default IssueForProductionFormModal;